import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import {
  Box,
  Typography,
  Stack,
  useTheme,
  Divider,
  Chip,
  Avatar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DevicesIcon from "@mui/icons-material/Devices";
import BusinessIcon from "@mui/icons-material/Business";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Title, Legend);

const LicenseCard = ({ licenseTypeSummary = {}, isSuperAdmin = false }) => {
  const theme = useTheme();

  const labels = Object.keys(licenseTypeSummary);

  const chartValues = labels.map(
    (key) => licenseTypeSummary[key]?.licenses_taken || 0
  );

  const totalLicenses = chartValues.reduce((a, b) => a + b, 0);

  const data = {
    labels: labels.map((l) => l.replace(/_/g, " ").toUpperCase()),
    datasets: [
      {
        label: "Licenses Taken",
        data: chartValues,
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.error.main,
          theme.palette.info.main,
        ],
        borderWidth: 0,
        barThickness: 22,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.raw} license(s) taken`,
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: 12 }, color: "#595C60" },
        grid: { display: false },
      },
      y: {
        ticks: { font: { size: 12 }, color: "#595C60" },
        grid: { display: false },
      },
    },
    elements: { bar: { borderRadius: 8 } },
  };

  return (
    <Box
      bgcolor="background.paper"
      p={3}
      borderRadius={3}
      minHeight="100%"
      boxShadow={4}
      sx={{
        transition: "0.3s",
        "&:hover": { boxShadow: 8 },
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ fontFamily: "Nunito Sans" }}
        >
          License Overview
        </Typography>

        <Chip
          label={`Total Licenses: ${totalLicenses}`}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Chart */}
      <Box width="100%" height={235} mb={3}>
        <Bar data={data} options={options} />
      </Box>

      {/* Detailed Summary */}
      <Stack spacing={2}>
        {labels.map((key, index) => {
          const item = licenseTypeSummary[key];
          const color = data.datasets[0].backgroundColor[index];

          const percent =
            item.devices_allowed > 0
              ? (item.devices_assigned / item.devices_allowed) * 100
              : 0;

          return (
            <Box
              key={key}
              p={2.5}
              borderRadius={2}
              sx={{
                background: `${color}08`,
                borderLeft: `5px solid ${color}`,
                boxShadow: 1,
              }}
            >
              {/* License Title */}
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Avatar sx={{ bgcolor: color, width: 28, height: 28 }}>
                  <CheckCircleIcon fontSize="small" />
                </Avatar>

                <Typography fontWeight={700} color={color}>
                  {key.replace(/_/g, " ").toUpperCase()}
                </Typography>
              </Stack>

              {/* Counts */}
              <Typography>
                <b>Licenses Taken:</b> {item.licenses_taken}
              </Typography>

              <Typography>
                <b>Allowed Devices:</b> {item.devices_allowed}
              </Typography>

              <Typography>
                <b>Assigned Devices:</b> {item.devices_assigned}
              </Typography>

              {isSuperAdmin && (
                <Typography>
                  <b>Companies Using This License:</b> {item.company_count}
                </Typography>
              )}

              {/* Progress Bar */}
              <Box mt={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12}>Utilization</Typography>
                  <Typography fontSize={12}>{percent.toFixed(1)}%</Typography>
                </Stack>

                <Box
                  mt={0.5}
                  height={8}
                  borderRadius={4}
                  bgcolor="#E4E4E4"
                  position="relative"
                  overflow="hidden"
                >
                  <Box
                    height="100%"
                    width={`${percent}%`}
                    bgcolor={color}
                    borderRadius={4}
                    sx={{ transition: "width 0.4s ease" }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default LicenseCard;
